import { useState, useEffect } from 'react';
import { useAuthStore } from '@/shared/stores/authStore';
import { Link } from 'react-router';
import {
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  TrendingUp,
  Package,
  Eye,
  RefreshCw
} from 'lucide-react';
import Header from '@/react-app/components/Header';
import { api } from '@/shared/api/endpoints';
import type { IUserFinancialResponse, ITransaction, IUserPackage } from '@/shared/types';
import { useNavigate } from "react-router-dom";


export default function Financials() {
  const { user: authStoreUser, isAuthenticated } = useAuthStore();
  const [financialData, setFinancialData] = useState<IUserFinancialResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'transactions' | 'packages'>('transactions');
  const navigate = useNavigate();

  useEffect(() => {
    if (authStoreUser) {
      fetchFinancialData();
    }
  }, [authStoreUser]);

  const fetchFinancialData = async () => {
    try {
      setLoading(true);
      const response = await api.user.getFinancialInfo();
      if (response.success && response.data) {
        setFinancialData(response.data);
      }
    } catch (error) {
      console.error('Error fetching financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'deposit':
        return <ArrowDownRight className="h-4 w-4 text-green-500" />;
      case 'monthly_roi':
        return <TrendingUp className="h-4 w-4 text-blue-500" />;
      case 'referral_bonus':
        return <ArrowUpRight className="h-4 w-4 text-purple-500" />;
      case 'investment_closure':
        return <Clock className="h-4 w-4 text-orange-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      completed: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
      active: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300',
      closed: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
    };

    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-300';
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  };

  // console.log(financialData?.data.transactions);
  console.log(financialData?.transactions);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header variant="dashboard" />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading financial data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    navigate("/login")
    return null;
  }

  const transactions = financialData?.transactions || [];
  const packages = financialData?.packages || [];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header variant="dashboard" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Financial</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1">
              Overview of your transactions and package investments
            </p>
          </div>
          <button
            onClick={fetchFinancialData}
            className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex">
              <button
                onClick={() => setActiveTab('transactions')}
                className={`px-6 py-4 font-medium text-sm transition-colors flex items-center space-x-2 ${activeTab === 'transactions'
                  ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
              >
                <Clock className="h-4 w-4" />
                <span>Transactions History</span>
                <span className="bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-full text-xs">
                  {transactions.length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('packages')}
                className={`px-6 py-4 font-medium text-sm transition-colors flex items-center space-x-2 ${activeTab === 'packages'
                  ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
              >
                <Package className="h-4 w-4" />
                <span>Packages Invested</span>
                <span className="bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-full text-xs">
                  {packages.length}
                </span>
              </button>
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'transactions' && (
              <div>
                {/* Transactions Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Transaction
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Date
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                      {transactions.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-6 py-8 text-center">
                            <Clock className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-500 dark:text-gray-400">No transactions found</p>
                          </td>
                        </tr>
                      ) : (
                        transactions.slice(0, 10).map((transaction: ITransaction) => (
                          <tr key={transaction._id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-gray-100 dark:bg-gray-600 rounded-full flex items-center justify-center">
                                  {getTransactionIcon(transaction.type)}
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900 dark:text-white capitalize">
                                    {transaction.type.replace('_', ' ')}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    ID: {transaction._id.slice(-8)}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="font-semibold text-gray-900 dark:text-white">
                                {formatCurrency(transaction.amount)}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300 capitalize">
                                {transaction.type.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(transaction.status)}`}>
                                {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                              {new Date(transaction.createdAt).toLocaleDateString()}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* View More Button */}
                {transactions.length > 0 && (
                  <div className="mt-6 text-center">
                    <Link
                      to="/transactions"
                      className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 space-x-2"
                    >
                      <Eye className="h-4 w-4" />
                      <span>View All Transactions</span>
                      <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'packages' && (
              <div>
                {/* Packages Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Package
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Amount saveed
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Monthly ROI
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Start Date
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                      {packages.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-6 py-8 text-center">
                            <Package className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-500 dark:text-gray-400">No packages found</p>
                          </td>
                        </tr>
                      ) : (
                        packages.slice(0, 10).map((pkg: IUserPackage) => (
                          <tr key={pkg._id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                                  <Package className="h-4 w-4 text-white" />
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900 dark:text-white">
                                    {pkg.packageId.name}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {pkg.packageId.roiPercentage}% ROI • {pkg.packageId.durationInMonths} months
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="font-semibold text-gray-900 dark:text-white">
                                {formatCurrency(pkg.amount)}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="font-semibold text-green-600">
                                {formatCurrency(pkg.monthlyROI)}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(pkg.status)}`}>
                                {pkg.status.charAt(0).toUpperCase() + pkg.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                              {new Date(pkg.startDate).toLocaleDateString()}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* View More Button */}
                {packages.length > 0 && (
                  <div className="mt-6 text-center">
                    <Link
                      to="/packages"
                      className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 space-x-2"
                    >
                      <Eye className="h-4 w-4" />
                      <span>View All Packages</span>
                      <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
